import React, { useState } from 'react';

/**
 * ExpandableText Component
 * Truncates long text blocks with a brand-styled "Read more" / "Read less" toggle.
 */
function ExpandableText({ text, maxChars = 350, maxWords = 60, className = "" }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const words = text.trim().split(/\s+/);
  const isLong = text.length > maxChars || words.length > maxWords;

  if (!isLong) {
    return <span className={className}>{text}</span>;
  }

  const getTruncatedContent = () => {
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(' ') + '...';
    }
    return text.slice(0, maxChars) + '...';
  };

  return (
    <span className={className}>
      {isExpanded ? text : getTruncatedContent()}{' '}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="inline-flex items-center gap-0.5 text-[12px] font-bold text-[var(--color-orange)] hover:text-[#c2410c] hover:opacity-85 focus:outline-none transition-colors ml-1 cursor-pointer"
      >
        {isExpanded ? 'Read less ▲' : 'Read more ▼'}
      </button>
    </span>
  );
}

/**
 * MultiAgentResultsView Component
 * Renders structured risk analysis results from /query-v2 Multi-Agent Orchestrator.
 * Displays audience-specific Executive View (prominently) and Engineering View (expandable/secondary).
 */
export default function MultiAgentResultsView({ analysisResult, downloadAnalysisPdf }) {
  const [showEngineeringView, setShowEngineeringView] = useState(false);

  if (!analysisResult) return null;

  const decisionReport = analysisResult.decision_report;
  const execView = decisionReport?.executive_view;
  const engView = decisionReport?.engineering_view;

  const isFallbackText = (text) => {
    if (!text) return true;
    const lower = text.toLowerCase();
    return lower.startsWith("no high-risk") ||
           lower.startsWith("no test case data") ||
           lower.startsWith("no historical matches") ||
           lower.startsWith("no recurring bug") ||
           lower.startsWith("all analyzed requirements");
  };

  const realRisks = (analysisResult.risks || []).filter(r => !isFallbackText(r));
  const realGaps = (analysisResult.coverage_gaps || []).filter(g => !isFallbackText(g));
  const realBugs = (analysisResult.recurring_bugs || []).filter(b => !isFallbackText(b));

  const getRiskBadge = (riskLevel) => {
    const level = (riskLevel || 'low').toLowerCase();
    if (level === 'critical') {
      return <span className="inline-block px-3.5 py-1 rounded-full text-[12px] font-bold bg-[#7f1d1d] text-white whitespace-nowrap shrink-0 w-fit">Critical Risk</span>;
    }
    if (level === 'high') {
      return <span className="inline-block px-3.5 py-1 rounded-full text-[12px] font-bold bg-[#fee2e2] text-[#dc2626] whitespace-nowrap shrink-0 w-fit">High Risk</span>;
    }
    if (level === 'medium' || level === 'amber') {
      return <span className="inline-block px-3.5 py-1 rounded-full text-[12px] font-bold bg-[#ffedd5] text-[#ea580c] whitespace-nowrap shrink-0 w-fit">Medium Risk</span>;
    }
    return <span className="inline-block px-3.5 py-1 rounded-full text-[12px] font-bold bg-[#dcfce7] text-[#16a34a] whitespace-nowrap shrink-0 w-fit">Low Risk</span>;
  };

  const getRecommendationBadge = (rec) => {
    const r = (rec || 'GO').toUpperCase();
    if (r === 'NO_GO') {
      return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[13px] font-extrabold bg-[#7f1d1d] text-white border border-[#991b1b] shadow-sm whitespace-nowrap shrink-0 w-fit">⛔ NO GO</span>;
    }
    if (r === 'CONDITIONAL_GO') {
      return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[13px] font-extrabold bg-[#ffedd5] text-[#c2410c] border border-[#fed7aa] shadow-sm whitespace-nowrap shrink-0 w-fit">⚠️ CONDITIONAL GO</span>;
    }
    return <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-[13px] font-extrabold bg-[#dcfce7] text-[#15803d] border border-[#bbf7d0] shadow-sm whitespace-nowrap shrink-0 w-fit">✓ GO FOR RELEASE</span>;
  };

  const getSeverityBadge = (severity) => {
    const sev = (severity || 'low').toLowerCase();
    if (sev === 'critical' || sev === 'high') {
      return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#fee2e2] text-[#dc2626] uppercase whitespace-nowrap shrink-0">High</span>;
    }
    if (sev === 'medium') {
      return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#ffedd5] text-[#ea580c] uppercase whitespace-nowrap shrink-0">Medium</span>;
    }
    return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#f3f4f6] text-[#4b5563] uppercase whitespace-nowrap shrink-0">Low</span>;
  };

  return (
    <div className="space-y-6">
      {/* Top Banner: Risk Evaluation Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-[var(--color-bg-alt)] p-6 rounded-[16px] border border-[var(--color-border)]">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-['Sora'] text-[22px] font-bold text-[var(--color-navy)]">
              Multi-Agent Risk Evaluation
            </h2>
            <span className="px-2.5 py-0.5 text-[11px] font-extrabold uppercase bg-purple-100 text-purple-700 rounded-full tracking-wider whitespace-nowrap">
              Orchestrated
            </span>
          </div>
          <p className="font-['Inter'] text-[13px] text-[var(--color-text-secondary)] mt-1">
            Synthesized insights across Requirement Intelligence, Defect History, Coverage Gap, and Decision Agent.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap shrink-0">
          {execView?.decision_recommendation
            ? getRecommendationBadge(execView.decision_recommendation)
            : getRiskBadge(analysisResult.risk_level)}
          {downloadAnalysisPdf && (
            <button
              type="button"
              onClick={downloadAnalysisPdf}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold border border-[var(--color-border)] text-[var(--color-navy)] hover:bg-white transition-colors whitespace-nowrap shrink-0"
            >
              📄 Download PDF
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* PROMINENT EXECUTIVE REPORT VIEW                                            */}
      {/* ========================================================================= */}
      <div className="space-y-5">
        {/* Executive Decision & Recommendation Banner */}
        <div className="p-6 rounded-[16px] bg-white border border-[var(--color-border)] shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center border-b border-[var(--color-border)] pb-3 gap-3 flex-wrap">
            <div>
              <div className="text-[11px] font-extrabold text-[var(--color-orange)] uppercase tracking-wider font-['Sora']">
                Executive Synthesis View
              </div>
              <h3 className="text-[18px] font-bold text-[var(--color-navy)] font-['Sora']">
                Release Decision Recommendation
              </h3>
            </div>
            <div className="flex items-center gap-3 text-[13px] font-medium text-[var(--color-text-secondary)] flex-wrap">
              {execView && (
                <>
                  <span>Score: <strong className="text-[var(--color-navy)]">{execView.overall_risk_score.toFixed(1)}/100</strong></span>
                  <span>•</span>
                  <span>Confidence: <strong className="text-[var(--color-navy)]">{(execView.overall_confidence * 100).toFixed(0)}%</strong></span>
                  <span>•</span>
                </>
              )}
              {getRiskBadge(execView?.risk_band || analysisResult.risk_level)}
            </div>
          </div>

          <div className="text-[14px] leading-relaxed text-[var(--color-text-primary)] font-['Inter'] whitespace-pre-line bg-[var(--color-bg-alt)] p-4 rounded-xl border border-[var(--color-border)]">
            <ExpandableText text={execView?.recommendation_rationale || analysisResult.summary} maxChars={350} maxWords={60} />
          </div>

          {/* Key Drivers & Next Actions */}
          {execView && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
              {/* Key Risk Drivers */}
              <div className="space-y-3">
                <h4 className="text-[13px] font-bold text-[var(--color-navy)] uppercase tracking-wider font-['Sora'] flex items-center gap-1.5">
                  🔥 Top Release Risk Drivers ({execView.key_drivers?.length || 0})
                </h4>
                {execView.key_drivers?.length > 0 ? (
                  <div className="space-y-2">
                    {execView.key_drivers.map((driver, idx) => (
                      <div key={idx} className="p-3 bg-[var(--color-bg-alt)] rounded-xl border border-[var(--color-border)] text-[12px] space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold text-[var(--color-navy)]">
                            <ExpandableText text={driver.statement} maxChars={200} maxWords={35} />
                          </span>
                          {getSeverityBadge(driver.severity)}
                        </div>
                        <div className="text-[11px] text-[var(--color-text-secondary)] font-mono">
                          ID: {driver.id} | Category: {driver.category}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 bg-[#dcfce7] text-[#16a34a] rounded-xl text-[12px] font-semibold">
                    ✓ No critical or high-severity risk drivers identified.
                  </div>
                )}
              </div>

              {/* Next Actions */}
              <div className="space-y-3">
                <h4 className="text-[13px] font-bold text-[var(--color-navy)] uppercase tracking-wider font-['Sora'] flex items-center gap-1.5">
                  ⚡ Prioritized Next Actions ({execView.next_actions?.length || 0})
                </h4>
                {execView.next_actions?.length > 0 ? (
                  <div className="space-y-2">
                    {execView.next_actions.map((act, idx) => (
                      <div key={idx} className="p-3 bg-[var(--color-bg-alt)] rounded-xl border border-[var(--color-border)] text-[12px] space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-bold text-[var(--color-navy)]">
                            {act.action_id}: <ExpandableText text={act.description} maxChars={200} maxWords={35} />
                          </span>
                          {getSeverityBadge(act.priority)}
                        </div>
                        <div className="text-[11px] text-[var(--color-text-secondary)] font-mono">
                          Target Finding(s): {act.target_finding_ids?.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 bg-[#dcfce7] text-[#16a34a] rounded-xl text-[12px] font-semibold">
                    ✓ No mandatory release blocking actions required.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* EXPANDABLE ENGINEERING REPORT VIEW                                        */}
      {/* ========================================================================= */}
      <div className="border border-[var(--color-border)] rounded-[16px] overflow-hidden bg-white shadow-sm">
        <button
          type="button"
          onClick={() => setShowEngineeringView(!showEngineeringView)}
          className="w-full bg-[var(--color-bg-alt)] px-6 py-4 border-b border-[var(--color-border)] flex justify-between items-center text-left hover:bg-slate-100 transition-colors focus:outline-none"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="font-['Sora'] text-[15px] font-bold text-[var(--color-navy)]">
                🛠️ Engineering & QA Detailed Report
              </span>
              <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-blue-100 text-blue-800 rounded-full">
                Technical View
              </span>
            </div>
            <p className="text-[12px] text-[var(--color-text-secondary)] font-['Inter'] mt-0.5">
              Full accepted findings, exact remediation actions, evidence references, and agent execution timeline.
            </p>
          </div>
          <span className="text-[14px] font-bold text-[var(--color-navy)]">
            {showEngineeringView ? '▲ Collapse Technical Details' : '▼ Expand Technical Details'}
          </span>
        </button>

        {showEngineeringView && (
          <div className="p-6 space-y-6 bg-white">
            {/* Agent Execution Timeline */}
            {engView?.agent_execution_timeline && (
              <div className="space-y-3">
                <h4 className="text-[13px] font-bold text-[var(--color-navy)] uppercase tracking-wider font-['Sora']">
                  ⏱️ Agent Execution Timeline & Pipeline Latency
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-[var(--color-bg-alt)] rounded-xl border border-[var(--color-border)] text-[12px]">
                    <div className="text-[var(--color-text-secondary)]">Total Pipeline Latency</div>
                    <div className="text-[16px] font-bold text-[var(--color-navy)] mt-0.5">
                      {engView.agent_execution_timeline.total_duration_seconds ?? 'N/A'}s
                    </div>
                  </div>
                  {Object.entries(engView.agent_execution_timeline.agents || {}).map(([agentName, meta]) => (
                    <div key={agentName} className="p-3 bg-[var(--color-bg-alt)] rounded-xl border border-[var(--color-border)] text-[12px]">
                      <div className="text-[var(--color-text-secondary)] capitalize">{agentName} Agent</div>
                      <div className="text-[14px] font-bold text-[var(--color-navy)] mt-0.5 flex items-center justify-between">
                        <span>{meta?.duration_seconds ?? 0}s</span>
                        <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded ${meta?.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {meta?.success ? 'SUCCESS' : 'FAILED'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Remediation Actions List */}
            {engView?.remediation_actions?.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-[13px] font-bold text-[var(--color-navy)] uppercase tracking-wider font-['Sora']">
                  🔧 Step-by-Step Remediation Actions ({engView.remediation_actions.length})
                </h4>
                <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-xl overflow-hidden">
                  {engView.remediation_actions.map((action, idx) => (
                    <div key={idx} className="p-4 space-y-2 bg-white">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className="font-mono text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                            {action.finding_id}
                          </span>
                          <span className="ml-2 font-bold text-[13px] text-[var(--color-navy)]">
                            Category: {action.category}
                          </span>
                        </div>
                        {getSeverityBadge(action.severity)}
                      </div>
                      <div className="text-[13px] text-[var(--color-text-primary)] font-['Inter']">
                        <strong>Remediation Steps:</strong>{' '}
                        <ExpandableText text={action.remediation_steps} maxChars={250} maxWords={40} />
                      </div>
                      {action.evidence_references?.length > 0 && (
                        <div className="mt-2 p-3 bg-[var(--color-bg-alt)] rounded-lg text-[12px] font-mono text-[var(--color-text-secondary)] border border-[var(--color-border)] space-y-1">
                          <div>
                            <strong>Evidence Quote:</strong> "{' '}
                            <ExpandableText text={action.evidence_references[0]?.quote} maxChars={250} maxWords={40} />
                            "
                          </div>
                          {action.evidence_references[0]?.source_id && (
                            <div><strong>Source ID:</strong> {action.evidence_references[0]?.source_id} ({action.evidence_references[0]?.source_type})</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3 Agent Categorized Findings */}
            <div className="space-y-4 pt-2">
              <h4 className="text-[13px] font-bold text-[var(--color-navy)] uppercase tracking-wider font-['Sora']">
                📋 Categorized Findings Across 3 Specialized Agents
              </h4>

              {/* Requirement Agent Findings */}
              <div className="border border-[var(--color-border)] rounded-xl overflow-hidden bg-white">
                <div className="bg-[var(--color-bg-alt)] px-4 py-3 border-b border-[var(--color-border)] flex justify-between items-center">
                  <span className="text-[13px] font-bold text-[var(--color-navy)]">📝 Requirement Agent Findings</span>
                  <span className="text-[12px] font-bold text-[var(--color-navy)]">{realRisks.length} Identified</span>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {realRisks.length > 0 ? (
                    realRisks.map((risk, index) => (
                      <div key={index} className="p-3 text-[13px] text-[var(--color-text-primary)]">
                        <ExpandableText text={risk} maxChars={250} maxWords={40} />
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-[12px] text-[var(--color-text-muted)] font-medium">
                      ✓ No high-risk requirement ambiguities identified.
                    </div>
                  )}
                </div>
              </div>

              {/* Test Coverage Agent Findings */}
              <div className="border border-[var(--color-border)] rounded-xl overflow-hidden bg-white">
                <div className="bg-[var(--color-bg-alt)] px-4 py-3 border-b border-[var(--color-border)] flex justify-between items-center">
                  <span className="text-[13px] font-bold text-[var(--color-navy)]">🧪 Test Coverage Agent Findings</span>
                  <span className="text-[12px] font-bold text-[var(--color-navy)]">{realGaps.length} Gaps</span>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {realGaps.length > 0 ? (
                    realGaps.map((gap, index) => (
                      <div key={index} className="p-3 text-[13px] text-[var(--color-text-primary)]">
                        <ExpandableText text={gap} maxChars={250} maxWords={40} />
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-[12px] text-[#16a34a] font-bold bg-[#dcfce7]">
                      ✓ Full verification coverage. All requirements have matching test cases.
                    </div>
                  )}
                </div>
              </div>

              {/* Historical Defect Agent Findings */}
              <div className="border border-[var(--color-border)] rounded-xl overflow-hidden bg-white">
                <div className="bg-[var(--color-bg-alt)] px-4 py-3 border-b border-[var(--color-border)] flex justify-between items-center">
                  <span className="text-[13px] font-bold text-[var(--color-navy)]">🐛 Historical Defect Agent Findings</span>
                  <span className="text-[12px] font-bold text-[var(--color-navy)]">{realBugs.length} Traced</span>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {realBugs.length > 0 ? (
                    realBugs.map((bug, index) => (
                      <div key={index} className="p-3 text-[13px] text-[var(--color-text-primary)]">
                        <ExpandableText text={bug} maxChars={250} maxWords={40} />
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-[12px] text-[var(--color-text-muted)] font-medium">
                      ✓ No recurring bug patterns traced against historical defect database.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Impacted Code Files (if code impact analysis requested) */}
      {analysisResult.impacted_files?.length > 0 && (
        <div className="border border-[var(--color-border)] rounded-[16px] overflow-hidden bg-white shadow-sm">
          <div className="bg-[var(--color-bg-alt)] px-6 py-3.5 border-b border-[var(--color-border)] flex justify-between items-center">
            <span className="text-[13px] font-bold text-[var(--color-navy)]">🧩 Impacted Code Repository Files</span>
            <span className="text-[12px] font-bold text-[var(--color-navy)]">
              {analysisResult.impacted_files.length} Files
            </span>
          </div>
          <div className="divide-y divide-[var(--color-border)] bg-white p-4 space-y-2">
            {analysisResult.impacted_files.map((path, index) => (
              <div key={index} className="p-2.5 bg-[var(--color-bg-alt)] rounded-lg font-mono text-[12px] text-[var(--color-navy)]">
                {path}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
